import { useState, useCallback, ImgHTMLAttributes, ReactNode } from 'react'
import { ImageOff } from 'lucide-react'

import { cn } from '@/common/lib/utils'

type ImageWithFallbackProps = Omit<
	ImgHTMLAttributes<HTMLImageElement>,
	'onError'
> & {
	/**
	 * Custom fallback element to render when image fails to load or src is empty
	 */
	fallback?: ReactNode
	/**
	 * Class name for the fallback container
	 */
	fallbackClassName?: string
	/**
	 * Whether to show loading state
	 */
	showLoading?: boolean
	/**
	 * Custom loading element
	 */
	loadingElement?: ReactNode
}

/**
 * Image component with automatic fallback when src is empty or fails to load.
 *
 * @example
 * // Basic usage - shows default placeholder on error
 * <ImageWithFallback src={imageUrl} alt="Preview" className="h-48 w-48" />
 *
 * @example
 * // Custom fallback
 * <ImageWithFallback
 *   src={imageUrl}
 *   alt="Preview"
 *   fallback={<Video className="h-12 w-12 text-muted-foreground" />}
 * />
 */
export function ImageWithFallback({
	src,
	alt,
	className,
	fallback,
	fallbackClassName,
	showLoading = false,
	loadingElement,
	...props
}: ImageWithFallbackProps) {
	const [hasError, setHasError] = useState(false)
	const [isLoading, setIsLoading] = useState(!!src && showLoading)

	const handleError = useCallback(() => {
		setHasError(true)
		setIsLoading(false)
	}, [])

	const handleLoad = useCallback(() => {
		setIsLoading(false)
	}, [])

	// Reset error state when src changes
	const [prevSrc, setPrevSrc] = useState(src)
	if (src !== prevSrc) {
		setPrevSrc(src)
		setHasError(false)
		setIsLoading(!!src && showLoading)
	}

	// Show fallback if no src or error occurred
	if (!src || hasError) {
		return (
			<div
				className={cn(
					'flex items-center justify-center bg-muted',
					fallbackClassName || className,
				)}
			>
				{fallback || <ImageOff className="h-8 w-8 text-muted-foreground" />}
			</div>
		)
	}

	// Show loading state
	if (isLoading && showLoading) {
		return (
			<div
				className={cn(
					'flex items-center justify-center bg-muted',
					fallbackClassName || className,
				)}
			>
				{loadingElement || (
					<div className="h-8 w-8 animate-pulse rounded-full bg-muted-foreground/20" />
				)}
			</div>
		)
	}

	return (
		<img
			src={src}
			alt={alt}
			className={className}
			onError={handleError}
			onLoad={handleLoad}
			{...props}
		/>
	)
}
